import type { Money } from '@fitting-room/contracts'

/**
 * `price` is the one attribute key the contract reserves by name
 * (`rangeFilterStepSchema.attributeKey` is `z.literal('price') | slug`), so
 * treating it as money is contract-sanctioned rather than domain knowledge.
 * Everything else is formatted through its attribute definition's `unit`.
 */
export const PRICE_KEY = 'price'

export function formatMoney(money: Money | undefined, locale: string): string {
  if (!money) return ''
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: money.currency,
    maximumFractionDigits: 0,
  }).format(money.amountMinor / 100)
}

/**
 * Range facets for `price` arrive in minor units, matching `moneySchema`.
 * The currency is not on the facet, so the caller passes whatever it has
 * learned from the catalogue; without it we degrade to a grouped number
 * rather than inventing a symbol.
 */
export function formatMinorAmount(amountMinor: number, locale: string, currency?: string): string {
  const major = amountMinor / 100
  if (currency) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(major)
  }
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(major)
}

export function formatFacetValue(
  attributeKey: string,
  value: number,
  locale: string,
  opts: { currency?: string; unit?: string } = {},
): string {
  if (attributeKey === PRICE_KEY) return formatMinorAmount(value, locale, opts.currency)
  const formatted = new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(value)
  return opts.unit ? `${formatted} ${opts.unit}` : formatted
}

export function formatDateTime(iso: string, locale: string, timeZone?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(locale, {
    dateStyle: 'medium',
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

/**
 * A calendar day — `YYYY-MM-DD` — read back to her in her own language.
 *
 * Formatted in UTC on purpose. A bare day parses as midnight UTC, so a browser
 * west of Greenwich would otherwise show her the day before the one she typed,
 * which on a screen about an appointment is not a rounding error.
 */
export function formatDay(day: string, locale: string): string {
  const date = new Date(`${day}T00:00:00.000Z`)
  if (Number.isNaN(date.getTime())) return day
  return new Intl.DateTimeFormat(locale, { dateStyle: 'long', timeZone: 'UTC' }).format(date)
}

export function formatTime(iso: string, locale: string, timeZone?: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso
  return new Intl.DateTimeFormat(locale, {
    timeStyle: 'short',
    ...(timeZone ? { timeZone } : {}),
  }).format(date)
}

export function isoDateToday(): string {
  const now = new Date()
  const y = now.getFullYear()
  const m = `${now.getMonth() + 1}`.padStart(2, '0')
  const d = `${now.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}
